import { useState, useEffect, useCallback, useRef } from 'react';

const LANGUAGE_CODES = {
  'TH': 'th-TH',
  'EN': 'en-US',
  'CN': 'zh-CN'
};

// Detect script of a single character.
// Using new RegExp() so the Unicode literal chars are written once in one place
// and the engine sees the already-decoded code-points (no file-encoding risk).
const THAI_RE  = new RegExp('[฀-๿]');
const CJK_RE   = new RegExp('[一-鿿㐀-䶿豈-﫿぀-ヿ]');
const LATIN_RE = /[a-zA-Z]/;

const getCharLang = (char) => {
  if (THAI_RE.test(char))  return 'th-TH';
  if (CJK_RE.test(char))   return 'zh-CN';
  if (LATIN_RE.test(char)) return 'en-US';
  return null;
};

// Split text into language-tagged segments.
// Neutral chars (digits, spaces, punctuation) stay with the current segment.
// defaultLangCode is used for any content that has no language context yet
// so that no segment ever carries lang:null.
const segmentByLanguage = (text, defaultLangCode) => {
  const segments = [];
  let current = '';
  let currentLang = null;

  for (const char of text) {
    const charLang = getCharLang(char);
    if (charLang && charLang !== currentLang) {
      if (current.trim()) {
        segments.push({ text: current, lang: currentLang || defaultLangCode });
      }
      current = char;
      currentLang = charLang;
    } else {
      current += char;
      if (charLang) currentLang = charLang;
    }
  }

  if (current.trim()) {
    segments.push({ text: current, lang: currentLang || defaultLangCode });
  }

  return segments.filter(s => s.text.trim().length > 0);
};

// Remove characters TTS engines read aloud awkwardly.
// Keeps: Thai (U+0E00-U+0E7F), CJK (U+4E00-U+9FFF), Kana (U+3040-U+30FF),
//        Latin letters, digits, whitespace, and basic sentence punctuation.
const STRIP_RE = new RegExp(
  '[^฀-๿一-鿿぀-ヿa-zA-Z0-9\\s.,!?:;\']',
  'g'
);

const cleanTextForTTS = (text) => {
  if (!text) return '';
  return text
    .replace(/https?:\/\/[^\s]+/g, '')
    .replace(STRIP_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────
const useTTS = (language = 'EN') => {
  const [isPlaying, setIsPlaying]               = useState(false);
  const [isSupported, setIsSupported]           = useState(false);
  const [availableVoices, setAvailableVoices]   = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState(null);
  const [audioOutputDevices, setAudioOutputDevices]   = useState([]);
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState('default');
  // Incremented each time reading completes NATURALLY (not via stop()).
  // Consumers watch this to auto-advance to the next item.
  const [readingFinished, setReadingFinished] = useState(0);

  const utteranceRef   = useRef(null);
  const lastSpeakTime  = useRef(0);
  const speakTokenRef  = useRef(0);   // incremented on every speak/stop to invalidate stale chains
  const cancelTimerRef = useRef(null); // setTimeout handle between cancel() and first speak()

  // ── Voice list ───────────────────────────────
  const updateVoices = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return false;
    const all = window.speechSynthesis.getVoices();
    if (all.length === 0) return false;

    const langCode  = LANGUAGE_CODES[language] || 'en-US';
    const prefix    = langCode.split('-')[0];

    const matching = all.filter(v =>
      v.lang.startsWith(prefix) ||
      v.lang.replace('_', '-').startsWith(prefix)
    );

    setAvailableVoices(matching.length > 0 ? matching : all);

    // selectedVoiceName must be a voice that actually matches this language;
    // never fall back to an English voice when language is TH/CN.
    const saved = localStorage.getItem('tvent_tts_voice_' + language);
    if (saved && matching.some(v => v.name === saved)) {
      setSelectedVoiceName(saved);
    } else if (matching.length > 0) {
      setSelectedVoiceName(matching[0].name);
    } else {
      setSelectedVoiceName(null);
    }

    return true;
  }, [language]);

  useEffect(() => {
    const ok =
      typeof window !== 'undefined' &&
      'speechSynthesis' in window &&
      typeof window.speechSynthesis.speak  === 'function' &&
      typeof window.speechSynthesis.cancel === 'function';
    setIsSupported(ok);

    if (ok) {
      const loaded = updateVoices();
      let poll = null;
      if (!loaded) {
        let n = 0;
        poll = setInterval(() => {
          n++;
          if (updateVoices() || n >= 10) clearInterval(poll);
        }, 500);
      }
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = updateVoices;
      }
      return () => { if (poll) clearInterval(poll); };
    }
  }, [updateVoices]);

  // ── Audio output devices ─────────────────────
  const updateOutputDevices = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const out  = devs.filter(d => d.kind === 'audiooutput');
      if (out.length > 0) {
        setAudioOutputDevices(out);
        const saved = localStorage.getItem('tvent_tts_output_device');
        if (saved && out.some(d => d.deviceId === saved)) setSelectedOutputDeviceId(saved);
      }
    } catch (_) { /* not supported */ }
  }, []);

  useEffect(() => {
    updateOutputDevices();
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', updateOutputDevices);
      return () => navigator.mediaDevices.removeEventListener('devicechange', updateOutputDevices);
    }
  }, [updateOutputDevices]);

  // ── Cancel on language change ─────────────────
  useEffect(() => {
    if (window.speechSynthesis && isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
    updateVoices();
  }, [language, updateVoices]);

  // ── Voice selector ────────────────────────────
  // Returns the best available voice for a BCP-47 code.
  // Saved preference is honoured only when it genuinely matches the language.
  const getVoiceForLang = useCallback((langCode) => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return null;

    const primaryCode = LANGUAGE_CODES[language] || 'en-US';
    const prefix      = langCode.split('-')[0];
    const matches     = (v) => v.lang === langCode || v.lang.replace('_', '-').startsWith(prefix);

    if (langCode === primaryCode && selectedVoiceName) {
      const pref = voices.find(v => v.name === selectedVoiceName);
      if (pref && matches(pref)) return pref;
    }

    return voices.find(v => v.lang === langCode) ||
           voices.find(v => matches(v)) ||
           null;
  }, [language, selectedVoiceName]);

  // ── speak ─────────────────────────────────────
  const speak = useCallback((text) => {
    if (!isSupported || !text) return;

    const now = Date.now();
    if (now - lastSpeakTime.current < 300) return;
    lastSpeakTime.current = now;

    // Invalidate any pending chain from a previous call
    speakTokenRef.current += 1;
    const myToken = speakTokenRef.current;

    if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
    window.speechSynthesis.cancel();

    const primaryLangCode = LANGUAGE_CODES[language] || 'en-US';
    const cleaned = cleanTextForTTS(text);
    if (!cleaned) return;

    const primaryVoice = getVoiceForLang(primaryLangCode);

    // ── Chrome cancel+speak bug fix ──────────────
    // Chrome silently drops speak() calls made synchronously after cancel().
    // A 100 ms gap lets the engine fully process the cancel before we enqueue.
    cancelTimerRef.current = setTimeout(() => {
      if (speakTokenRef.current !== myToken) return;

      utteranceRef.current = true;
      setIsPlaying(true);

      // ── Strategy A: segmented (best pronunciation) ──────────────────────
      // Only when the primary language has a confirmed voice in getVoices().
      // If we segment without a Thai voice, Chrome silently drops the Thai
      // utterances and the chain stalls — that is the root cause of Thai being
      // skipped entirely.
      if (primaryVoice) {
        const segments = segmentByLanguage(cleaned, primaryLangCode);
        if (segments.length === 0) { setIsPlaying(false); return; }

        const speakNext = (index) => {
          if (speakTokenRef.current !== myToken) return;

          if (index >= segments.length) {
            setIsPlaying(false);
            utteranceRef.current = null;
            setReadingFinished(n => n + 1);
            return;
          }

          const seg = segments[index];
          const u   = new SpeechSynthesisUtterance(seg.text);
          u.lang    = seg.lang;
          u.rate    = 1.0;
          u.pitch   = 1.0;

          const v = getVoiceForLang(seg.lang);
          if (v) {
            u.voice = v;
          } else {
            // No language-specific voice for this segment — fall back to the
            // primary voice so the utterance doesn't silently fail.
            u.voice = primaryVoice;
            u.lang  = primaryLangCode;
          }

          u.onend   = () => speakNext(index + 1);
          u.onerror = (e) => {
            if (e.error === 'interrupted') return;
            console.warn('[TTS] segment error (' + seg.lang + '):', e.error);
            speakNext(index + 1);
          };

          window.speechSynthesis.speak(u);
        };

        speakNext(0);
        return;
      }

      // ── Strategy B: single utterance (no primary voice in getVoices()) ──
      // When no Thai/CN/etc. voice is in the local getVoices() list, Chrome's
      // online voices (Google Thai, etc.) may still be accessible via the
      // lang attribute — but ONLY if we don't force a mismatched voice object.
      // Reading the whole block as one utterance gives Chrome the best chance
      // to auto-route to its online TTS engine.
      const u   = new SpeechSynthesisUtterance(cleaned);
      u.lang    = primaryLangCode;
      u.rate    = 1.0;
      u.pitch   = 1.0;
      // intentionally no u.voice — let browser auto-select

      u.onend   = () => {
        if (speakTokenRef.current !== myToken) return;
        setIsPlaying(false);
        utteranceRef.current = null;
        setReadingFinished(n => n + 1);
      };
      u.onerror = (e) => {
        if (e.error === 'interrupted') return;
        const loaded = window.speechSynthesis.getVoices();
        console.error(
          '[TTS] no voice for lang=' + primaryLangCode +
          ' | voices loaded=' + loaded.length +
          ' | error=' + e.error +
          '\nTo fix: install the ' + language + ' language pack in Windows Settings > Time & Language > Language'
        );
        setIsPlaying(false);
        utteranceRef.current = null;
      };

      window.speechSynthesis.speak(u);
    }, 100); // 100 ms gap — Chrome cancel+speak fix

  }, [isSupported, language, getVoiceForLang]);

  // ── stop ──────────────────────────────────────
  const stop = useCallback(() => {
    if (!isSupported) return;
    speakTokenRef.current += 1;
    if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    utteranceRef.current = null;
    lastSpeakTime.current = 0;
  }, [isSupported]);

  const setVoice = useCallback((voiceName) => {
    setSelectedVoiceName(voiceName);
    localStorage.setItem('tvent_tts_voice_' + language, voiceName);
    if (window.speechSynthesis) { window.speechSynthesis.cancel(); setIsPlaying(false); }
  }, [language]);

  const setOutputDevice = useCallback((deviceId) => {
    setSelectedOutputDeviceId(deviceId);
    localStorage.setItem('tvent_tts_output_device', deviceId);
    if (window.speechSynthesis) { window.speechSynthesis.cancel(); setIsPlaying(false); }
    lastSpeakTime.current = 0;
  }, []);

  return {
    speak, stop, isPlaying, isSupported,
    availableVoices, selectedVoiceName, setVoice,
    audioOutputDevices, selectedOutputDeviceId, setOutputDevice,
    readingFinished
  };
};

export default useTTS;
